import React from "react";
import { 
  Server, 
  Shield, 
  RefreshCw, 
  Palette, 
  Bell,
  Star
} from "lucide-react";
import { useLanguage } from "@/i18n";

const benefitIcons = [Server, Shield, RefreshCw, Palette, Bell];

const ChatDemoSection = () => {
  const { t } = useLanguage();
  const benefits = t('chatDemo.benefits');

  return (
    <section className="py-20 sm:py-28 px-4">
      <div className="max-w-7xl mx-auto">
        {/* Section header */}
        <div className="text-center mb-16 space-y-4">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-display font-bold text-foreground">
            {t('chatDemo.title')}{" "}
            <span className="text-gradient">{t('chatDemo.titleHighlight')}</span>
          </h2>
          <p className="text-muted-foreground text-lg max-w-2xl mx-auto">
            {t('chatDemo.description')}
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center">
          {/* Benefits list */}
          <div className="space-y-4 order-2 lg:order-1">
            {benefits.map((benefit, index) => {
              const Icon = benefitIcons[index];
              return (
                <div
                  key={index}
                  className="flex items-start gap-4 p-4 rounded-xl bg-card/50 border border-border/50 hover:border-primary/30 transition-colors duration-300"
                >
                  <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                    <Icon className="w-5 h-5 text-primary" />
                  </div>
                  <p className="text-foreground pt-2">{benefit}</p>
                </div>
              );
            })}
          </div>

          {/* Chat demo */}
          <div className="order-1 lg:order-2">
            <div className="relative">
              {/* Glow */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/20 via-secondary/10 to-accent/20 blur-3xl scale-110" />
              
              {/* Chat window */}
              <div className="relative glass-card rounded-3xl p-6 sm:p-8">
                {/* Chat header */}
                <div className="flex items-center gap-3 pb-6 border-b border-border/50">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-primary to-accent flex items-center justify-center">
                    <span className="text-primary-foreground font-bold">C</span>
                  </div>
                  <div>
                    <p className="font-semibold text-foreground">{t('chatDemo.chatProvider')}</p>
                    <p className="text-xs text-muted-foreground">{t('chatDemo.chatStatus')}</p>
                  </div>
                  <div className="ml-auto w-2 h-2 bg-green-500 rounded-full" />
                </div>

                {/* Messages */}
                <div className="py-6 space-y-4 min-h-[280px]">
                  {/* User message */}
                  <div className="flex justify-end animate-fade-in">
                    <div className="bg-primary text-primary-foreground rounded-2xl rounded-br-md px-4 py-3 max-w-[85%]">
                      <p className="text-sm">{t('hero.chatUser')}</p>
                    </div>
                  </div>
                  
                  {/* AI response */}
                  <div className="flex justify-start animate-fade-in" style={{ animationDelay: '0.3s' }}>
                    <div className="bg-muted rounded-2xl rounded-bl-md px-4 py-3 max-w-[90%]">
                      <p className="text-sm text-foreground mb-3">{t('hero.chatAiIntro')}</p>
                      <ul className="space-y-2">
                        <li className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="text-primary font-bold">1.</span>
                          {t('hero.chatAiPoint1')}
                        </li>
                        <li className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="text-primary font-bold">2.</span>
                          {t('hero.chatAiPoint2')}
                        </li>
                        <li className="flex items-start gap-2 text-sm text-muted-foreground">
                          <span className="text-primary font-bold">3.</span>
                          {t('hero.chatAiPoint3')}
                        </li>
                      </ul>
                    </div>
                  </div>

                  {/* Command */}
                  <div className="flex justify-end animate-fade-in" style={{ animationDelay: '0.6s' }}>
                    <div className="flex items-center gap-2 bg-secondary/20 border border-secondary/30 rounded-xl px-3 py-2">
                      <Star className="w-4 h-4 text-secondary" />
                      <code className="text-sm text-secondary">{t('hero.chatCommand')}</code>
                    </div>
                  </div>

                  {/* System response */}
                  <div className="flex justify-start animate-fade-in" style={{ animationDelay: '0.9s' }}>
                    <div className="bg-accent/20 border border-accent/30 rounded-xl px-4 py-2">
                      <p className="text-sm text-accent flex items-center gap-2">
                        <Star className="w-4 h-4" />
                        {t('hero.chatCommandResponse')}
                      </p>
                    </div>
                  </div>
                </div>

                {/* Input */}
                <div className="pt-4 border-t border-border/50">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 bg-muted rounded-xl px-4 py-3 text-sm text-muted-foreground">
                      {t('chatDemo.chatPlaceholder')}
                    </div>
                    <button className="w-10 h-10 bg-primary rounded-xl flex items-center justify-center hover:bg-primary/90 transition-colors">
                      <svg className="w-5 h-5 text-primary-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 19l9 2-9-18-9 18 9-2zm0 0v-8" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ChatDemoSection;
